import { Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  FileTextIcon,
  GlobeIcon,
  ListChecksIcon,
  PencilLineIcon,
  PlusIcon,
} from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { FormsListQueryDTO } from "@/modules/forms/domain/contracts/forms";
import { useFormsListQuery } from "@/modules/forms/presentation/api/queries";
import {
  FormsDashboardQuickLinksCard,
  quickLinkIcons,
} from "@/modules/forms/presentation/ui/dashboard/FormsDashboardQuickLinksCard";
import { FormsDashboardStatCard } from "@/modules/forms/presentation/ui/dashboard/FormsDashboardStatCard";
import { FormsRecentFormsCard } from "@/modules/forms/presentation/ui/dashboard/FormsRecentFormsCard";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

export function FormsDashboardPage() {
  const nav = useNavigate({ from: "/app/forms/dashboard" });
  const canCreate = useActionPermission("forms:create");
  const canUpdate = useActionPermission("forms:update");

  const qTotal = useFormsListQuery({ limit: 1, offset: 0 });
  const qDraft = useFormsListQuery({
    status: "draft",
    limit: 1,
    offset: 0,
  } satisfies FormsListQueryDTO);
  const qPublished = useFormsListQuery({
    status: "published",
    limit: 1,
    offset: 0,
  } satisfies FormsListQueryDTO);
  const qClosed = useFormsListQuery({
    status: "closed",
    limit: 1,
    offset: 0,
  } satisfies FormsListQueryDTO);
  const qRecent = useFormsListQuery({ limit: 5, offset: 0 });

  const total = qTotal.data?.meta.total ?? 0;
  const published = qPublished.data?.meta.total ?? 0;

  const publishedHint = useMemo(() => {
    if (qTotal.isLoading || total <= 0) return undefined;
    const pct = Math.round((published / total) * 100);
    return `${pct}% ຂອງຟອມທັງໝົດ`;
  }, [published, qTotal.isLoading, total]);

  const goToForms = (status?: FormsListQueryDTO["status"]) => {
    void nav({
      to: "/app/forms",
      search: status
        ? { limit: 20, offset: 0, status }
        : { limit: 20, offset: 0 },
    });
  };

  const quickLinks = useMemo(() => {
    const items = [
      {
        title: "ຟອມທັງໝົດ",
        description: "ລາຍການ ກອງ ແລະ ຈັດການຟອມ public",
        icon: quickLinkIcons.list,
        onClick: () => goToForms(),
      },
    ];
    if (canUpdate) {
      items.push({
        title: "ຫມວດຫມູ່ຟອມ",
        description: "ຈັດກຸ່ມຟອມຕາມປະເພດ",
        icon: quickLinkIcons.categories,
        onClick: () => {
          void nav({ to: "/app/forms/categories" });
        },
      });
    }
    if (canCreate) {
      items.push({
        title: "ສ້າງຟອມໃໝ່",
        description: "ເລີ່ມຮ່າງຟອມ ແລະ ເພີ່ມຄຳຖາມ",
        icon: quickLinkIcons.new,
        onClick: () => {
          void nav({ to: "/app/forms/new" });
        },
      });
    }
    return items;
  }, [canCreate, canUpdate, nav]);

  return (
    <>
      <Header>
        <Breadcrumb items={[{ label: "ແຜງຟອມ" }]} />
      </Header>
      <Main>
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 space-y-2">
              <h1 className="font-semibold text-2xl tracking-tight">ແຜງຟອມ</h1>
              <p className="text-muted-foreground text-sm">
                ສະຫຼຸບສະຖານະຟອມ · ເຂົ້າໜ້າຈັດການ ແລະ ເບິ່ງຜົນຕອບ
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => goToForms()}
              >
                <ListChecksIcon className="mr-2 h-4 w-4" />
                ຟອມທັງໝົດ
              </Button>
              {canCreate ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => nav({ to: "/app/forms/new" })}
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  ຟອມໃໝ່
                </Button>
              ) : null}
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            <FormsDashboardStatCard
              title="ຟອມທັງໝົດ"
              value={total}
              isLoading={qTotal.isLoading}
              icon={FileTextIcon}
              tone="default"
              onClick={() => goToForms()}
            />
            <FormsDashboardStatCard
              title="ຮ່າງ"
              value={qDraft.data?.meta.total ?? 0}
              isLoading={qDraft.isLoading}
              icon={PencilLineIcon}
              tone="draft"
              onClick={() => goToForms("draft")}
            />
            <FormsDashboardStatCard
              title="ເປີດຮັບ"
              value={published}
              isLoading={qPublished.isLoading}
              icon={GlobeIcon}
              tone="published"
              hint={publishedHint}
              onClick={() => goToForms("published")}
            />
            <FormsDashboardStatCard
              title="ປິດ"
              value={qClosed.data?.meta.total ?? 0}
              isLoading={qClosed.isLoading}
              icon={ArchiveIcon}
              tone="closed"
              onClick={() => goToForms("closed")}
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <FormsRecentFormsCard
                forms={qRecent.data?.data ?? []}
                isLoading={qRecent.isLoading}
                onViewAll={() => goToForms()}
                onEdit={(id) =>
                  nav({ to: "/app/forms/$id/edit", params: { id } })
                }
                onResponses={(id) =>
                  nav({ to: "/app/forms/$id/responses", params: { id } })
                }
              />
            </div>
            <FormsDashboardQuickLinksCard links={quickLinks} />
          </div>
        </div>
      </Main>
    </>
  );
}
